import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import mixpanel from "@/mixpanel";
import { api } from "@/lib/api";
import { reportError } from "@/lib/errors";
import {
  Phone, Heart, Users, Lightbulb, MapPin, Clock,
  Briefcase, GraduationCap, ArrowRight, CheckCircle2, Star, Quote,
  ChevronLeft, ChevronRight, ShieldCheck, Linkedin, Stethoscope, Menu
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const EzLogo = ({ size = "h-8" }) => (
  <img
    src="https://customer-assets.emergentagent.com/job_health-guide-jobs/artifacts/u6wy1bb4_EZ%20HEALTH%20LOGO%20COLOR%20ALPHA%20SMALL.png"
    alt="Ez Health"
    className={`${size} w-auto`}
    data-testid="ez-logo"
  />
);

const AboutUsLink = () => {
  const href = withIndeedUser("https://ez.health/en/about");
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm font-semibold text-ez-teal hover:text-ez-blue transition-colors font-heading"
      data-testid="about-us-link"
    >
      About us
    </a>
  );
};

// When the current page was opened from an Indeed SMS (?user=<short_id>
// on the URL), we forward the same param onto every cross-page nav link so
// downstream pages keep rendering the "Coach" phrasing instead of falling
// back to the default "Guide" copy. For external URLs (About us on
// ez.health) the param rides along as a query string; for internal links
// that already use a hash (Our Coaches → /#our-coaches) we splice the
// query in between path and hash. Returns the input untouched when no
// prefill is present, so direct visitors see clean URLs.
const withIndeedUser = (href) => {
  if (typeof window === "undefined") return href;
  const userId = new URLSearchParams(window.location.search).get("user");
  if (!userId) return href;
  const encoded = encodeURIComponent(userId);
  // Split href into [base, hash] so the query slots between them, then
  // pick "?" vs "&" depending on whether base already has a query string.
  const hashIdx = href.indexOf("#");
  const hash = hashIdx >= 0 ? href.slice(hashIdx) : "";
  const base = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}user=${encoded}${hash}`;
};

const AvoidScamsLink = () => (
  <a
    href="/avoid-scams"
    target="_blank"
    rel="noopener noreferrer"
    className="text-sm font-semibold text-ez-teal hover:text-ez-blue transition-colors font-heading"
    data-testid="avoid-scams-link"
  >
    Avoid scams
  </a>
);

const ContactUsLink = () => (
  <a
    href="/contact-us"
    target="_blank"
    rel="noopener noreferrer"
    className="text-sm font-semibold text-ez-teal hover:text-ez-blue transition-colors font-heading"
    data-testid="contact-us-link"
  >
    Contact us
  </a>
);

// Scrolls to the "From Our Team" testimonial section on the Guide landing
// page. Behavior depends on where the candidate currently is:
//   • On the landing page: prevent default, smooth-scroll in-page, sync hash.
//   • On any other Guide page (apply, willo, thank-you, avoid-scams): open
//     /#our-coaches in a new tab. LandingPage's hash-scroll effect picks up
//     the `#our-coaches` hash on mount and scrolls.
// Label: "Our coaches" when the visitor came from an Indeed SMS (?user=
//   prefill in the URL — i.e. they applied to a "Health Coach"-style title),
//   otherwise "Our guides" (matches the public role title on the landing
//   hero). The check reads the *current* page's URL each render so the label
//   stays correct after route changes that retain the query string.
const OurCoachesLink = () => {
  const onLanding = typeof window !== "undefined" && window.location.pathname === "/";
  const hasIndeedPrefill = typeof window !== "undefined"
    && !!new URLSearchParams(window.location.search).get("user");
  const label = hasIndeedPrefill ? "Our Coaches" : "Our Guides";

  const onClick = (e) => {
    if (onLanding) {
      e.preventDefault();
      const el = document.getElementById("our-coaches");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", "#our-coaches");
      }
    }
    // Off the landing page we fall through with target="_blank" — the new
    // tab loads "/" with the hash and LandingPage's effect handles the scroll.
  };

  return (
    <a
      href={withIndeedUser("/#our-coaches")}
      onClick={onClick}
      target={onLanding ? undefined : "_blank"}
      rel={onLanding ? undefined : "noopener noreferrer"}
      className="text-sm font-semibold text-ez-teal hover:text-ez-blue transition-colors font-heading"
      data-testid="our-coaches-link"
    >
      {label}
    </a>
  );
};

// Single source of truth for the team-photo grid used on the landing page and
// in the survey trust interstitial. Update names/photos/links here only.
const COACHES = [
  {
    name: "Katie D.",
    joined: "Joined Ez Health Nov 2025",
    photo: "https://customer-assets.emergentagent.com/job_1be00376-6806-4e12-b7b0-d4475ced2f92/artifacts/1gjbn1w9_image.png",
    linkedin: "https://www.linkedin.com/in/katiejoyduke/",
  },
  {
    name: "Hannah F.",
    joined: "Joined Ez Health Jan 2026",
    photo: "https://customer-assets.emergentagent.com/job_1be00376-6806-4e12-b7b0-d4475ced2f92/artifacts/yryf416k_image.png",
    linkedin: "https://www.linkedin.com/in/hannahflicker/",
  },
  {
    name: "Jamie Z.",
    joined: "Joined Ez Health Jan 2026",
    photo: "https://customer-assets.emergentagent.com/job_1be00376-6806-4e12-b7b0-d4475ced2f92/artifacts/lwegw21n_image.png",
    linkedin: "https://www.linkedin.com/in/jamiezwier/",
  },
  {
    name: "Kenzie B.",
    joined: "Joined Ez Health Feb 2026",
    photo: "https://customer-assets.emergentagent.com/job_1be00376-6806-4e12-b7b0-d4475ced2f92/artifacts/hqyywjm4_image.png",
    linkedin: "https://www.linkedin.com/in/kenzie-barnhart-71b657331/",
  },
];

const CoachCard = ({ coach, index = 0 }) => (
  <motion.a
    href={coach.linkedin}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.08, duration: 0.4 }}
    className="group block bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all"
    data-testid={`coach-card-${coach.name.toLowerCase().replace(/[^a-z]/g, "")}`}
    aria-label={`${coach.name} on LinkedIn`}
  >
    <div className="relative aspect-square overflow-hidden bg-ez-surface">
      <img
        src={coach.photo}
        alt={coach.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="flex items-center justify-between p-4">
      <div className="min-w-0">
        <p className="font-semibold text-ez-teal font-heading tracking-tight truncate">{coach.name}</p>
        <p className="text-xs text-slate-500 font-body mt-0.5">{coach.joined}</p>
      </div>
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] group-hover:bg-[#0A66C2] group-hover:text-white transition-colors flex-shrink-0 ml-2">
        <Linkedin className="w-4 h-4" />
      </span>
    </div>
  </motion.a>
);

// Inline link strip used in the top nav on md+ screens. Hidden on mobile.
const NavDesktopLinks = () => (
  <div className="hidden md:flex items-center gap-5 lg:gap-7" data-testid="nav-menu-desktop">
    <AboutUsLink />
    <AvoidScamsLink />
    <OurCoachesLink />
    <ContactUsLink />
  </div>
);

// Mobile-only hamburger button + slide-in sheet. Rendered as the leftmost
// item in each header on mobile (before the logo), and the sheet slides in
// from the left edge — matches the reference layout. The Apply CTA in each
// header stays outside this component, on the right edge of the nav bar.
const NavMobileToggle = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open navigation menu"
          className="md:hidden inline-flex items-center justify-center w-9 h-9 -ml-1 text-ez-teal hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="nav-menu-toggle"
        >
          <Menu className="w-6 h-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <SheetTitle className="sr-only">Site navigation</SheetTitle>
        <nav
          className="flex flex-col gap-1 pt-16 px-2"
          onClick={() => setOpen(false)}
          data-testid="nav-menu-mobile"
        >
          <a
            href={withIndeedUser("https://ez.health/en/about")}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-3 rounded-xl text-base font-semibold text-ez-teal hover:bg-ez-surface transition-colors font-heading"
            data-testid="mobile-about-us-link"
          >
            About us
          </a>
          <a
            href="/avoid-scams"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-3 rounded-xl text-base font-semibold text-ez-teal hover:bg-ez-surface transition-colors font-heading"
            data-testid="mobile-avoid-scams-link"
          >
            Avoid scams
          </a>
          <MobileOurCoachesItem />
          <a
            href="/contact-us"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-3 rounded-xl text-base font-semibold text-ez-teal hover:bg-ez-surface transition-colors font-heading"
            data-testid="mobile-contact-us-link"
          >
            Contact us
          </a>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

// Back-compat shim for any header that hasn't migrated to the
// split components yet. Renders both, so layout is identical to before.
const NavMenu = () => (
  <>
    <NavDesktopLinks />
    <NavMobileToggle />
  </>
);

// Mobile drawer entry for the Our Guides/Coaches link. Mirrors the desktop
// link's behavior — in-page smooth scroll when on landing, otherwise opens
// /#our-coaches in a new tab — and uses the same Indeed-prefill label.
const MobileOurCoachesItem = () => {
  const onLanding = typeof window !== "undefined" && window.location.pathname === "/";
  const hasIndeedPrefill = typeof window !== "undefined"
    && !!new URLSearchParams(window.location.search).get("user");
  const label = hasIndeedPrefill ? "Our Coaches" : "Our Guides";
  const onClick = (e) => {
    if (onLanding) {
      e.preventDefault();
      const el = document.getElementById("our-coaches");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", "#our-coaches");
      }
    }
  };
  return (
    <a
      href={withIndeedUser("/#our-coaches")}
      onClick={onClick}
      target={onLanding ? undefined : "_blank"}
      rel={onLanding ? undefined : "noopener noreferrer"}
      className="px-4 py-3 rounded-xl text-base font-semibold text-ez-teal hover:bg-ez-surface transition-colors font-heading"
      data-testid="mobile-our-coaches-link"
    >
      {label}
    </a>
  );
};

export { EzLogo, AboutUsLink, AvoidScamsLink, OurCoachesLink, ContactUsLink, NavMenu, NavDesktopLinks, NavMobileToggle, COACHES, CoachCard, CLIENT_TESTIMONIALS };

const CLIENT_TESTIMONIALS = [
  {
    quote: "My guide helped me understand my Medicare options in a way no one else could. She didn't just read me a list — she actually listened and found solutions I didn't know existed.",
    name: "Judith",
    age: 74,
    location: "Tampa, FL",
    detail: "Saved $320/mo on prescriptions",
  },
  {
    quote: "After my husband passed, I felt completely lost dealing with insurance and doctors. My guide called me every week — not because she had to, but because she genuinely cared. She helped me find grief counseling covered by my plan that I never knew about.",
    name: "Patricia",
    age: 81,
    location: "Phoenix, AZ",
    detail: "Connected to 4 new support services",
  },
  {
    quote: "I was paying for three supplements that actually conflicted with my medications. My guide caught that in our very first call and helped me talk to my doctor about it. I feel safer knowing someone is really paying attention.",
    name: "Margaret",
    age: 77,
    location: "Chicago, IL",
    detail: "Medication plan fully reviewed",
  },
  {
    quote: "I'm not great with computers, and the healthcare system feels like it was designed for people half my age. My guide walks me through everything on the phone — appointments, claims, even finding a new dentist. She makes it simple.",
    name: "Helen",
    age: 83,
    location: "Boston, MA",
    detail: "12 months with her guide",
  },
  {
    quote: "My daughter lives across the country and worries about me constantly. Since I started working with my guide, my daughter says she finally sleeps at night. That alone is worth everything.",
    name: "Dorothy",
    age: 79,
    location: "Denver, CO",
    detail: "Family peace of mind",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [smsFirstName, setSmsFirstName] = useState("");
  // The job title the candidate originally applied to on Indeed (e.g. "Health
  // Coach", "Wellness Coach"). Parsed by the backend from the inbound Indeed
  // notification email's subject line and surfaced via /indeed-prefill. Falls
  // back to "Health Coach" if the prefill request fails or doesn't return a
  // title — same default we use for the utm_campaign on the server side.
  const [jobTitle, setJobTitle] = useState("Health Coach");
  // When the URL has a ?user= param we hold the page from rendering until the
  // prefill request resolves so we don't briefly show the generic copy and
  // then snap to the personalized variant. Capped at 3s so a slow/failed
  // backend can never block the page indefinitely.
  const userIdFromQuery = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("user")
    : null;
  // Matches the navbar's "Our Guides" / "Our Coaches" decision: Indeed
  // visitors (?user= prefill) see "coaches" wording (their Indeed listing
  // was a "Health Coach"-style title), direct visitors see "guides" (the
  // public role title on this funnel).
  const indeedRoleLabel = userIdFromQuery ? "Coaches" : "Guides";
  const [prefillResolved, setPrefillResolved] = useState(!userIdFromQuery);

  useEffect(() => { mixpanel.track("landingPageViewed"); }, []);

  // If ?user= param exists, fetch prefill data for personalized welcome
  useEffect(() => {
    if (!userIdFromQuery) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let settled = false;
    const settle = () => { if (!settled) { settled = true; setPrefillResolved(true); } };
    // 3-second hard ceiling: if the prefill API hasn't responded by then we
    // render the page anyway with the default jobTitle ("Health Coach").
    const timeoutId = setTimeout(settle, 3000);

    api.get(`${backendUrl}/api/indeed-prefill/${userIdFromQuery}`)
      .then(r => r.data)
      .then(data => {
        if (data.found && data.name) {
          const first = data.name.trim().split(" ")[0];
          setSmsFirstName(first.charAt(0).toUpperCase() + first.slice(1).toLowerCase());
        }
        if (data.found && data.job_title) {
          setJobTitle(data.job_title);
        }
        if (data.found && data.email) {
          mixpanel.identify(data.email);
          mixpanel.people.set({ $email: data.email, $name: data.name || "", source: "indeed" });
        }
      })
      .catch((err) => reportError(err))
      .finally(() => {
        clearTimeout(timeoutId);
        settle();
      });
    return () => clearTimeout(timeoutId);
  }, [userIdFromQuery]);

  // Honor `#our-coaches` (and any future `#section-id`) in the URL once the
  // page is allowed to render. This makes cross-page nav from the "Our
  // guides/coaches" navbar link work even when the target route opens in a
  // brand-new tab — the hash is set, prefill resolves, then we scroll. We
  // also guard on `prefillResolved` so we don't try to scroll a hidden tree.
  useEffect(() => {
    if (!prefillResolved) return;
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    // Defer one tick so layout (above-fold images, section animations) has
    // committed real heights before we compute scroll offsets.
    const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [prefillResolved]);

  useEffect(() => {
    if (!autoRotate) return;
    const timer = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % CLIENT_TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [autoRotate]);

  const goToTestimonial = (dir) => {
    setAutoRotate(false);
    setTestimonialIndex(prev => {
      if (dir === "next") return (prev + 1) % CLIENT_TESTIMONIALS.length;
      return (prev - 1 + CLIENT_TESTIMONIALS.length) % CLIENT_TESTIMONIALS.length;
    });
    // Resume auto-rotate after 10s of inactivity
    setTimeout(() => setAutoRotate(true), 10000);
  };

  const handleFirstQuestion = (answer) => {
    mixpanel.track("applicationStarted", { source: "hero_question", answer: answer ? "yes" : "no" });
    const params = window.location.search;
    navigate(`/apply${params}`, { state: { can_work_25_hours: answer } });
  };

  const responsibilities = [
    { icon: <Phone className="w-5 h-5" />, title: "Work directly with clients", desc: "Build meaningful relationships with seniors over the phone, understanding their unique needs." },
    { icon: <Heart className="w-5 h-5" />, title: "Educate & empower", desc: "Help clients understand Medicare benefits, care options, and community resources." },
    { icon: <Lightbulb className="w-5 h-5" />, title: "Solve problems creatively", desc: "Assist seniors accessing meals, transport, specialists, and staying independent." },
    { icon: <Users className="w-5 h-5" />, title: "Collaborate across teams", desc: "Work with doctors, insurers, and social services to ensure clients get support." },
  ];

  const offers = [
    "No prior healthcare experience needed — comprehensive training provided",
    "Work directly with founders who scaled a healthcare business to $3.4B+",
    "Make a real difference in underserved seniors' lives",
    "Contract role with flexible scheduling",
    "Professional development support",
    "Be part of a startup from the ground up",
  ];

  const idealFor = [
    "Health coaches", "Teachers & educators", "Customer service professionals",
    "Social workers", "Wellness coaches", "Anyone with empathy & drive"
  ];

  // Render gate: when the URL has ?user= (Indeed-sourced visitor) we wait
  // for the prefill request to resolve — or for the 3s timeout — before
  // painting anything, so the personalized hero copy & jobTitle land in the
  // first render the candidate sees. Direct visitors (no ?user=) flip the
  // flag synchronously at mount and skip this branch entirely.
  if (!prefillResolved) {
    return <div className="min-h-screen bg-white" data-testid="landing-prefill-loading" />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-9" />
            <NavDesktopLinks />
          </div>
          <Button
            data-testid="header-apply-btn"
            onClick={() => navigate(`/apply${window.location.search}`)}
            className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-6 font-semibold shadow-lg shadow-blue-100"
          >
            {smsFirstName ? "Finish Application" : "Apply in 2 Minutes"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#EDF5FB] to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeUp} className="space-y-8">
              {smsFirstName ? (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ez-teal font-heading leading-[1.1]">
                    Welcome <span className="text-ez-blue">{smsFirstName}</span>
                  </h1>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl font-body">
                    Based on your background, you could be a strong fit
                    for Ez Health, helping seniors stay healthy
                    &amp; independent.
                  </p>
                  <div>
                    <p className="text-base font-semibold text-ez-teal font-heading mb-4">Take 2 minutes to finish your application now.</p>
                    <button
                      data-testid="hero-finish-btn"
                      onClick={() => navigate(`/apply${window.location.search}`)}
                      className="px-10 py-4 rounded-full text-base font-semibold font-heading bg-ez-blue/80 text-white hover:bg-ez-blue shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                    >
                      Finish My Application
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ez-teal font-heading leading-[1.1]">
                    Become a <span className="text-ez-blue">Health Guide</span>
                  </h1>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl font-body">
                    Help older adults navigate the complex healthcare system. No healthcare experience needed —
                    just compassion, creativity, and a love for helping people.
                  </p>
                  <div>
                    <p className="text-base font-semibold text-ez-teal font-heading mb-4">Are you able to work at least 28 hours per week?</p>
                    <div className="flex gap-3 max-w-xs">
                      <button
                        data-testid="hero-yes-btn"
                        onClick={() => handleFirstQuestion(true)}
                        className="flex-1 py-3.5 rounded-full text-base font-semibold font-heading bg-ez-blue text-white hover:bg-[#5A9BCF] shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                      >
                        Yes
                      </button>
                      <button
                        data-testid="hero-no-btn"
                        onClick={() => handleFirstQuestion(false)}
                        className="flex-1 py-3.5 rounded-full text-base font-semibold font-heading bg-white text-ez-teal border-2 border-slate-200 hover:border-ez-blue/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                      >
                        No
                      </button>
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center gap-6 pt-2 text-sm text-slate-500 font-body">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> 100% Remote</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> 30-40 hrs/week</span>
                <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> Contract</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-100 aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1683272468278-3ba6633c15a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Health guide supporting senior"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <Star className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-ez-teal text-sm font-heading">Founded by Noom veterans</p>
                    <p className="text-xs text-slate-500 font-body">Scaled to $3.4B+ in healthcare</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Carousel */}
      <section className="py-20 lg:py-24 bg-white" data-testid="client-testimonial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Real Stories</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">The Impact You'll Make</h2>
            <p className="text-base text-slate-500 font-body mt-3">We've helped <span className="font-semibold text-ez-teal">2,000+ seniors</span> all across the US — rated <a href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638/customer-reviews" target="_blank" rel="noopener noreferrer" className="font-semibold text-ez-teal hover:text-ez-blue transition-colors duration-200" data-testid="bbb-rating-link">4.97 on the BBB</a>.</p>
          </motion.div>

          <div className="max-w-4xl mx-auto relative" data-testid="testimonial-carousel">
            <div className="bg-ez-surface rounded-3xl p-8 md:p-12 border border-slate-100 relative overflow-hidden min-h-[280px]">
              <Quote className="w-16 h-16 text-ez-blue/10 absolute top-6 left-6" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={testimonialIndex}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35 }}
                  className="relative"
                >
                  <p className="text-lg md:text-xl text-ez-teal font-heading leading-relaxed mb-8" data-testid="testimonial-quote">
                    {CLIENT_TESTIMONIALS[testimonialIndex].quote}
                  </p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-12 h-12 rounded-full bg-ez-blue/10 flex items-center justify-center">
                      <span className="text-ez-blue font-bold text-lg font-heading">
                        {CLIENT_TESTIMONIALS[testimonialIndex].name[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-ez-teal font-heading" data-testid="testimonial-name">
                        {CLIENT_TESTIMONIALS[testimonialIndex].name}, {CLIENT_TESTIMONIALS[testimonialIndex].age}
                      </p>
                      <p className="text-sm text-slate-500 font-body">
                        Ez Health Client — {CLIENT_TESTIMONIALS[testimonialIndex].location}
                      </p>
                    </div>
                    <div className="ml-auto hidden sm:block">
                      <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-semibold font-body" data-testid="testimonial-detail">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {CLIENT_TESTIMONIALS[testimonialIndex].detail}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Carousel Controls */}
            <div className="flex items-center justify-between mt-6">
              <button
                data-testid="testimonial-prev-btn"
                onClick={() => goToTestimonial("prev")}
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-ez-blue hover:border-ez-blue transition-colors duration-200"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2" data-testid="testimonial-dots">
                {CLIENT_TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`testimonial-dot-${i}`}
                    onClick={() => {
                      setAutoRotate(false);
                      setTestimonialIndex(i);
                      setTimeout(() => setAutoRotate(true), 10000);
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === testimonialIndex ? "w-8 bg-ez-blue" : "w-2 bg-slate-300 hover:bg-slate-400"
                    }`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next-btn"
                onClick={() => goToTestimonial("next")}
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-ez-blue hover:border-ez-blue transition-colors duration-200"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Responsibilities */}
      <section id="role-details" className="py-20 lg:py-28 bg-white" data-testid="responsibilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">The Role</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">What you'll do</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {responsibilities.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="flex gap-5 p-8 bg-ez-surface rounded-2xl border border-slate-100 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-ez-teal text-lg mb-2 font-heading">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed font-body text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / 3rd-party validation — sits between client testimonials and the
          job-offer benefits section. Three independent proof points. */}
      <section className="py-16 lg:py-20 bg-white border-y border-slate-100" data-testid="trust-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">
              Verified by the BBB and Medicare
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <motion.a
              href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638/"
              target="_blank"
              rel="noopener noreferrer"
              {...fadeUp}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="group relative bg-white rounded-2xl border border-slate-200 p-6 lg:p-7 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all hover:-translate-y-0.5"
              data-testid="trust-bbb-rating"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-body mb-1">
                    Current BBB Rating
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-ez-teal font-heading tracking-tight leading-none">A</span>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" aria-label="Verified" />
                  </div>
                  <p className="text-sm text-slate-500 font-body mt-2 group-hover:text-ez-blue transition-colors">
                    View BBB profile <ArrowRight className="w-3.5 h-3.5 inline -mt-0.5 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all" />
                  </p>
                </div>
              </div>
            </motion.a>

            <motion.a
              href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638/customer-reviews"
              target="_blank"
              rel="noopener noreferrer"
              {...fadeUp}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="group relative bg-white rounded-2xl border border-slate-200 p-6 lg:p-7 shadow-sm hover:shadow-lg hover:border-amber-200 transition-all hover:-translate-y-0.5"
              data-testid="trust-bbb-reviews"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500 group-hover:bg-amber-100 transition-colors">
                  <Star className="w-6 h-6 fill-amber-400 stroke-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-body mb-1">
                    Customer reviews
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-ez-teal font-heading tracking-tight leading-none">4.99</span>
                    <span className="text-sm text-slate-500 font-body">avg rating</span>
                  </div>
                  <p className="text-sm text-slate-500 font-body mt-2 group-hover:text-ez-blue transition-colors">
                    Read reviews <ArrowRight className="w-3.5 h-3.5 inline -mt-0.5 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all" />
                  </p>
                </div>
              </div>
            </motion.a>

            <motion.a
              href="https://www.medicare.gov/care-compare/details/group-practice/7214469816?city=North%20Miami&state=FL&zipcode=33181"
              target="_blank"
              rel="noopener noreferrer"
              {...fadeUp}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="group relative bg-white rounded-2xl border border-slate-200 p-6 lg:p-7 shadow-sm hover:shadow-lg hover:border-ez-blue/30 transition-all hover:-translate-y-0.5"
              data-testid="trust-medicare"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue group-hover:bg-ez-blue/15 transition-colors">
                  <Stethoscope className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-body mb-1">
                    Approved Medicare provider
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-base font-bold text-ez-teal font-heading tracking-tight leading-tight">Listed on Medicare.gov</span>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" aria-label="Verified" />
                  </div>
                  <p className="text-sm text-slate-500 font-body mt-2 group-hover:text-ez-blue transition-colors">
                    View on Medicare.gov <ArrowRight className="w-3.5 h-3.5 inline -mt-0.5 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all" />
                  </p>
                </div>
              </div>
            </motion.a>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 lg:py-28 bg-ez-surface" data-testid="offers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Benefits</p>
              <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-8">What Ez Health Offers You</h2>
              <div className="space-y-4">
                {offers.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-body">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_health-guide-jobs/artifacts/p0jzx01v_beach-dog.jpg"
                alt="Woman with dog on the beach"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-20 lg:py-28 bg-white" data-testid="ideal-candidate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Who Fits</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-4">This Role is Perfect For</h2>
            <p className="text-base md:text-lg text-slate-600 mb-12 max-w-2xl mx-auto font-body">
              We prefer "healthcare outsiders" — warm, empathetic people who can explain complex topics simply, without jargon.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {idealFor.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.3 }}
                className="bg-ez-surface rounded-xl px-5 py-4 border border-slate-100 hover:border-ez-blue/30 transition-colors duration-200"
              >
                <div className="flex items-center justify-center gap-2">
                  <GraduationCap className="w-4 h-4 text-ez-blue" />
                  <span className="font-medium text-ez-teal text-sm font-body">{item}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guide Testimonial */}
      <section id="our-coaches" className="scroll-mt-20 py-20 lg:py-24 bg-ez-surface" data-testid="guide-testimonial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">From Our Team</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">
              Meet some of our {indeedRoleLabel}
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 max-w-5xl mx-auto">
            {COACHES.map((c, i) => (
              <CoachCard key={c.name} coach={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Job Details Strip */}
      <section className="py-12 bg-ez-teal" data-testid="job-details-strip">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: "Location", value: "100% Remote" },
            { label: "Rate", value: "$28–40/hr" },
            { label: "Hours", value: "30-40 hrs/week" },
            { label: "Schedule", value: "Flexible" },
          ].map((item, i) => (
            <div key={i}>
              <p className="text-ez-peach text-xs font-semibold uppercase tracking-wider mb-1 font-body">{item.label}</p>
              <p className="text-white font-semibold text-lg font-heading">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-6">
              Ready to Make a Difference?
            </h2>
            <p className="text-base md:text-lg text-slate-600 mb-4 font-body">
              It takes just 2 minutes to apply. No cover letter needed — just a few quick questions and your resume.
            </p>
            <p className="text-sm text-slate-500 font-body italic mb-10">
              "Joining Ez Health was the best career decision I ever made. I finally feel like my work matters." — Maria R., Joined March 2026
            </p>
            <Button
              data-testid="cta-apply-btn"
              onClick={() => navigate(`/apply${window.location.search}`)}
              className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-10 py-6 text-lg font-semibold shadow-xl shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
              size="lg"
            >
              Start Your Application <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-ez-surface border-t border-slate-100" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <EzLogo size="h-6" />
              <p className="text-sm text-slate-500 font-body">Making healthcare easier for seniors, one client at a time.</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-body">
              <a href="https://www.medicare.gov/care-compare/details/group-practice/7214469816?id=ez-recruitment&city=North%20Miami&state=FL&zipcode=33181" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-medicare-link">Medicare</a>
              <a href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-bbb-link">BBB</a>
              <a href="https://www.linkedin.com/company/ez-health-inc" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-linkedin-link">LinkedIn</a>
              <a href="https://www.facebook.com/people/EZ-Health/61565359091888/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-facebook-link">Facebook</a>
              <span className="text-slate-300">|</span>
              <a href="/contact-us" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-contact-us-link">Contact us</a>
              <a href="/privacy" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/terms" className="text-slate-500 hover:text-ez-blue transition-colors duration-200" data-testid="footer-terms-link">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
